import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import SelectBox from "../../atoms/select";
import type { SignUpInput } from "../../../types/auth.types";
import { cn } from "../../../lib/utils/cn";
import { useSidoList, useSigunguList } from "../../../@apis/hooks/useRegion";
import { useMyInfoStore } from "../../../lib/store/useMyInfoStore";
import { getZodiacImg } from "../../../lib/utils/birthdayUtils";
import { vw } from "../../../lib/utils/responsiveUtils.ts";
import { updateRegion } from "../../../@apis/v1/auth.service";
import type { UpdateRegionRequest } from "../../../@apis/dto/request/auth.request";
import type { OnErrorResponse } from "../../../@apis/dto/response/common.response.ts";
import { useModalStore } from "../../../lib/store/useModalStore.tsx";

export default function PreferredRegionScreen() {
  const { myInfo, setMyInfo } = useMyInfoStore();
  const { openModal } = useModalStore();

  const { watch, setValue } = useFormContext<SignUpInput>();

  const updateRegionMutation = useMutation({
    mutationFn: (form: UpdateRegionRequest) => updateRegion(form),
    onSuccess: (_res, variables) => {
      // 저장 성공 시 store에도 반영 (다른 화면에서 store 값을 사용)
      setMyInfo({
        sidoCode: variables.sidoCode,
        sidoName: variables.sidoName,
        sigunguCode: variables.sigunguCode,
        sigunguName: variables.sigunguName,
      });
    },
    onError: (err: OnErrorResponse) => {
      const { error } = err.response.data;
      openModal("ALERT_DIALOG", {
        title: "알림",
        content: error?.message ?? "선호 지역 저장에 실패했습니다.",
      });
    },
  });
  const sidoCode = watch("sidoCode") ?? "";
  const sigunguCode = watch("sigunguCode") ?? "";
  // 가입 성공 시 setMyInfo로 채워지므로 스토어 값을 그대로 사용
  const displayName = myInfo.name;
  const displayBirthdate = myInfo.birthdate;
  const zodiacImg =
    displayBirthdate.length === 6 ? getZodiacImg(displayBirthdate) : "";

  const { data: sidoData } = useQuery({ ...useSidoList() });
  const { data: sigunguData, isFetching: isSigunguFetching } = useQuery({
    ...useSigunguList(sidoCode),
  });

  const sidoOptions = useMemo(
    () => [
      { label: "시/도 선택", value: "" },
      ...(sidoData?.data ?? []).map(({ code, name }) => ({
        label: name,
        value: code,
      })),
    ],
    [sidoData?.data],
  );

  const sigunguOptions = useMemo(
    () => [
      {
        label: isSigunguFetching ? "불러오는 중" : "시/군/구 선택",
        value: "",
      },
      ...(sigunguData?.data ?? []).map(({ code, name }) => ({
        label: name,
        value: code,
      })),
    ],
    [isSigunguFetching, sigunguData?.data],
  );

  return (
    <div>
      <div className="flex gap-x-[12px] items-center mb-[30px]">
        <div
          className="rounded-full bg-[#f5f5f5]"
          style={{ width: vw(64), height: vw(64) }}
        >
          {zodiacImg && <img src={zodiacImg} alt="띠 이미지" />}
        </div>
        <p className="font-medium text-xl">
          {displayName} 님,
          <br /> 가입을 축하드립니다!
        </p>
      </div>
      <div className="space-y-[4px] mb-[20px]">
        <h1 className="font-semibold">추가 정보를 입력해 주세요.</h1>
        <p className="text-sm text-[#8e8e93]">
          선호 지역을 입력하시면, 회원님을 위한 맞춤 골프장을 추천해 드려요!
        </p>
      </div>
      <div className="space-y-[8px]">
        <p className="text-base font-bold">선호 지역 (선택)</p>
        <div className="flex items-center gap-x-[16px]">
          {/* 시/도 선택 */}
          <SelectBox
            options={sidoOptions}
            value={sidoCode}
            onChange={(e) => {
              const selectedCode = e.target.value;
              const selectedName =
                sidoData?.data.find(({ code }) => code === selectedCode)
                  ?.name ?? "";

              setValue("sidoCode", selectedCode, { shouldValidate: true });
              setValue("sidoName", selectedName, { shouldValidate: true });
              setValue("sigunguCode", "", { shouldValidate: true });
              setValue("sigunguName", "", { shouldValidate: true });

              updateRegionMutation.mutate({
                sidoCode: selectedCode,
                sidoName: selectedName,
                sigunguCode: "",
                sigunguName: "",
              });
            }}
            className={cn(
              "text-[0.7rem] placeholder:text-[0.7rem] w-full",
              sidoCode === "" ? "text-[#888]" : "",
            )}
          />
          {/* 시/군/구 선택 */}
          <SelectBox
            options={sigunguOptions}
            value={sigunguCode}
            onChange={(e) => {
              const selectedCode = e.target.value;
              const selectedName =
                sigunguData?.data.find(({ code }) => code === selectedCode)
                  ?.name ?? "";

              setValue("sigunguCode", selectedCode, { shouldValidate: true });
              setValue("sigunguName", selectedName, { shouldValidate: true });

              updateRegionMutation.mutate({
                sidoCode,
                sidoName: watch("sidoName") ?? "",
                sigunguCode: selectedCode,
                sigunguName: selectedName,
              });
            }}
            disabled={!sidoCode || isSigunguFetching}
            className={cn(
              "text-[0.7rem] placeholder:text-[0.7rem] w-full",
              sigunguCode === "" ? "text-[#888]" : "",
            )}
          />
        </div>
      </div>
    </div>
  );
}
